import React, { useState } from "react";
import { commissionRepo, invoiceRepo, staffRepo } from "../../services";
import { useApp } from "../../context/AppContext";
import { useI18n } from "../../i18n";
import { Button, DataTable, Input, Modal, PageHead, PersonCell, Select, StatCard, StatusBadge, baht, useAsync, useTableState } from "../../components/ui";
import Icon from "../../components/ui/Icon";

export default function Commissions() {
  const { orgId, toast } = useApp();
  const { tx } = useI18n();
  const tableState = useTableState();
  const [status, setStatus] = useState("");

  const { data, loading, reload } = useAsync(
    () => commissionRepo.list(orgId, { page: tableState.page, perPage: tableState.perPage, filter: (row) => !status || row.status === status }),
    [orgId, status, tableState.page, tableState.perPage]
  );
  const { data: all, reload: reloadAll } = useAsync(() => commissionRepo.all(orgId), [orgId], []);

  const { data: staff } = useAsync(() => staffRepo.all(orgId), [orgId], []);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [calc, setCalc] = useState({ from: "", to: "" });

  const thisMonth = new Date().toISOString().slice(0, 7);
  const openForm = () => {
    setCalc({ from: `${thisMonth}-01`, to: new Date().toISOString().slice(0, 10) });
    setForm({ staffId: "", baseAmount: "", rate: "", refDoc: "", period: thisMonth, invoiceIds: [] });
  };
  const round2 = (n) => Math.round(n * 100) / 100;
  const previewAmount = form && Number(form.baseAmount) > 0 && Number(form.rate) >= 0 && form.rate !== "" ? round2((Number(form.baseAmount) * Number(form.rate)) / 100) : null;

  const pickStaff = (staffId) => {
    const member = (staff || []).find((row) => row.id === staffId);
    setForm({ ...form, staffId, invoiceIds: [], rate: member?.commissionRate !== undefined && member?.commissionRate !== null ? String(member.commissionRate) : form.rate });
  };

  // คำนวณฐานยอดจากใบแจ้งหนี้ที่ชำระแล้วของพนักงานในช่วงวันที่ (ใบเสร็จไม่เก็บพนักงาน จึงอ่านจากใบแจ้งหนี้ต้นทาง)
  // ข้ามใบที่เคยถูกนับในรายการคอมของพนักงานคนเดียวกันแล้ว กันนับซ้ำ
  const calculate = async () => {
    if (!form.staffId) return toast(tx("เลือกพนักงานก่อน", "Select a staff member first"), "error");
    if (!calc.from || !calc.to || calc.to < calc.from) return toast(tx("เลือกช่วงวันที่ให้ถูกต้อง", "Select a valid date range"), "error");
    const used = new Set((all || []).filter((row) => row.staffId === form.staffId).flatMap((row) => (Array.isArray(row.invoiceIds) ? row.invoiceIds : [])));
    const invoices = await invoiceRepo.all(
      orgId,
      (row) => row.status === "paid" && row.staffId === form.staffId && String(row.paidAt || "").slice(0, 10) >= calc.from && String(row.paidAt || "").slice(0, 10) <= calc.to && !used.has(row.id)
    );
    if (!invoices.length) return toast(tx("ไม่พบใบแจ้งหนี้ที่ชำระแล้วและยังไม่ถูกคิดคอมในช่วงนี้", "No paid, uncounted invoices in this range"), "error");
    const base = round2(invoices.reduce((sum, row) => sum + (Number(row.total) || 0), 0));
    const docs = invoices.map((row) => row.docNo).filter(Boolean);
    setForm({
      ...form,
      baseAmount: String(base),
      invoiceIds: invoices.map((row) => row.id),
      refDoc: docs.length > 3 ? `${docs.slice(0, 3).join(", ")} +${docs.length - 3}` : docs.join(", "),
      period: calc.to.slice(0, 7),
    });
    toast(tx(`คำนวณจาก ${invoices.length} ใบแจ้งหนี้ ยอดรวม ${baht(base)}`, `Calculated from ${invoices.length} invoices: ${baht(base)}`), "success");
  };

  const saveForm = async () => {
    const member = (staff || []).find((row) => row.id === form.staffId);
    const base = Number(form.baseAmount);
    const rate = Number(form.rate);
    if (!member) return toast(tx("เลือกพนักงาน", "Select a staff member"), "error");
    if (!(base > 0)) return toast(tx("ฐานยอดต้องมากกว่า 0", "Base amount must be greater than 0"), "error");
    if (form.rate === "" || !(rate >= 0 && rate <= 100)) return toast(tx("อัตราคอมต้องอยู่ระหว่าง 0-100%", "Rate must be between 0 and 100%"), "error");
    if (!/^\d{4}-\d{2}$/.test(form.period)) return toast(tx("เลือกงวดเดือน", "Select the period month"), "error");
    setSaving(true);
    try {
      await commissionRepo.create(orgId, {
        staffId: member.id,
        staffName: member.name,
        position: member.position,
        refDoc: form.refDoc.trim() || "-",
        baseAmount: base,
        rate,
        amount: round2((base * rate) / 100),
        period: form.period,
        invoiceIds: form.invoiceIds,
        status: "pending",
        branchId: member.branchId,
        createdAt: new Date().toISOString(),
      });
      setForm(null);
      reload();
      reloadAll();
      toast(tx("สร้างรายการคอมมิชชันแล้ว", "Commission created"), "success");
    } catch (error) {
      toast(error?.message || tx("บันทึกไม่สำเร็จ", "Save failed"), "error");
    } finally {
      setSaving(false);
    }
  };

  const totalPending = (all || []).filter((row) => row.status !== "paid").reduce((sum, row) => sum + (Number(row.amount) || 0), 0);

  const approve = async (row, next) => {
    await commissionRepo.update(orgId, row.id, { status: next });
    reload();
    reloadAll();
    toast(tx("อัปเดตสถานะคอมมิชชันแล้ว", "Commission status updated"), "success");
  };

  return (
    <>
      <PageHead
        title={tx("แดชบอร์ดคอมมิชชัน", "Commission dashboard")}
        subtitle={tx("สรุปค่าคอมมิชชันของเซลส์ นายหน้า และช่าง", "Commission summary for sales, agents and technicians")}
        actions={<Button variant="primary" onClick={openForm}>+ {tx("เพิ่มรายการคอม", "Add commission")}</Button>}
      />

      <div className="stat-grid">
        <StatCard icon={<Icon name="money" />} label={tx("รอจ่ายรวม", "Pending payout")} value={baht(totalPending)} />
        <StatCard icon={<Icon name="staff" />} label={tx("รายการทั้งหมด", "Total records")} value={(all || []).length} />
      </div>

      <div className="filter-bar">
        <Select options={["pending", "approved", "paid"]} value={status} onChange={(e) => setStatus(e.target.value)} placeholder={tx("ทุกสถานะ", "All status")} style={{ width: "auto" }} />
      </div>

      <DataTable
        loading={loading}
        rows={data?.rows || []}
        total={data?.total || 0}
        page={tableState.page}
        perPage={tableState.perPage}
        onPage={tableState.setPage}
        onPerPage={tableState.setPerPage}
        emptyText={tx("ยังไม่มีข้อมูลคอมมิชชัน", "No commission records")}
        columns={[
          { key: "staffName", label: tx("พนักงาน", "Staff"), render: (row) => <PersonCell name={row.staffName} sub={row.position} /> },
          { key: "refDoc", label: tx("อ้างอิง", "Reference") },
          { key: "baseAmount", label: tx("ยอดฐาน", "Base"), align: "right", render: (row) => baht(row.baseAmount, 0) },
          { key: "rate", label: tx("อัตรา", "Rate"), align: "right", render: (row) => `${row.rate}%` },
          { key: "amount", label: tx("คอมมิชชัน", "Commission"), align: "right", render: (row) => <span className="u-strong">{baht(row.amount)}</span> },
          { key: "status", label: tx("สถานะ", "Status"), render: (row) => <StatusBadge status={row.status} /> },
          {
            key: "action",
            label: "",
            render: (row) =>
              row.status !== "paid" ? (
                <Button size="sm" variant="primary" onClick={() => approve(row, row.status === "pending" ? "approved" : "paid")}>
                  {row.status === "pending" ? tx("อนุมัติ", "Approve") : tx("จ่ายแล้ว", "Mark paid")}
                </Button>
              ) : null,
          },
        ]}
      />

      <Modal
        open={!!form}
        title={tx("เพิ่มรายการคอมมิชชัน", "New commission")}
        onClose={() => setForm(null)}
        footer={<><Button onClick={() => setForm(null)}>{tx("ยกเลิก", "Cancel")}</Button><Button variant="primary" disabled={saving} onClick={saveForm}>{tx("บันทึก", "Save")}</Button></>}
      >
        {form ? (
          <>
            <Select
              label={tx("พนักงาน", "Staff")}
              placeholder={tx("เลือกพนักงาน", "Select staff")}
              options={(staff || []).map((row) => ({ value: row.id, label: `${row.name}${row.position ? ` · ${row.position}` : ""}` }))}
              value={form.staffId}
              onChange={(e) => pickStaff(e.target.value)}
            />
            <div className="u-fs-12 u-muted u-mb-8">{tx("คำนวณฐานยอดจากใบแจ้งหนี้ที่ชำระแล้วของพนักงาน (ไม่บังคับ — หรือกรอกเองด้านล่าง)", "Optionally calculate the base from the staff member's paid invoices — or type it below")}</div>
            <div className="filter-bar">
              <Input type="date" value={calc.from} onChange={(e) => setCalc({ ...calc, from: e.target.value })} />
              <Input type="date" value={calc.to} onChange={(e) => setCalc({ ...calc, to: e.target.value })} />
              <Button onClick={calculate}>{tx("คำนวณ", "Calculate")}</Button>
            </div>
            <Input label={tx("ฐานยอด (บาท)", "Base amount (THB)")} type="number" min="0" value={form.baseAmount} onChange={(e) => setForm({ ...form, baseAmount: e.target.value, invoiceIds: [] })} />
            <Input label={tx("อัตราคอมมิชชัน (%)", "Rate (%)")} type="number" min="0" max="100" step="0.1" value={form.rate} onChange={(e) => setForm({ ...form, rate: e.target.value })} />
            <Input label={tx("อ้างอิง (เลขที่เอกสาร / หมายเหตุ)", "Reference")} value={form.refDoc} onChange={(e) => setForm({ ...form, refDoc: e.target.value })} />
            <Input label={tx("งวดเดือน", "Period (month)")} type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} />
            {previewAmount !== null ? <div className="u-fs-13 u-mt-8">{tx("ค่าคอมที่ได้", "Commission")} <span className="u-strong">{baht(previewAmount)}</span></div> : null}
          </>
        ) : null}
      </Modal>
    </>
  );
}
